/**
 * @fileoverview UI Updater
 * Automatically updates cart UI elements when cart changes
 */

import { SELECTORS, CSS_CLASSES, CART_EVENTS } from '../constants/index.js';
import { DOM, Debug, Performance, Format, smartUpdater } from '../utils/index.js';

/**
 * UI Updater class - Handles automatic UI updates for cart elements
 * @class
 */
export class UIUpdater {
  /**
   * @param {CartManager} cartManager - Cart manager instance
   * @param {CartConfig} [config] - Configuration options
   */
  constructor(cartManager, config = {}) {
    if (!cartManager) {
      throw new Error('CartManager is required for UIUpdater');
    }

    this.cartManager = cartManager;
    this.config = config;
    this.isInitialized = false;
    this.updateCount = 0;
    this.lastUpdate = null;
    this.updateQueue = new Map(); // Changed to Map to store operation IDs
    this.processingOperationId = null;
    
    // Bind methods
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    
    // Initialize with debounced update
    this.debouncedUpdate = Performance.debounce(() => {
      this.processUpdateQueue();
    }, 100); // 100ms debounce

    this.init();
  }

  /**
   * Initialize UI updater
   * @private
   */
  init() {
    if (this.isInitialized) {
      Debug.log('warn', 'UIUpdater already initialized');
      return;
    }
    
    // Listen to cart events
    this.cartManager.on(CART_EVENTS.UPDATED, this.handleCartUpdate);
    this.cartManager.on(CART_EVENTS.LOADED, this.handleCartUpdate);
    this.cartManager.on(CART_EVENTS.ADDED, this.handleCartUpdate);
    
    this.isInitialized = true;
    Debug.log('info', 'Cart UI auto-update system initialized');
  }

  /**
   * Handle cart update events
   * @private
   * @param {Object} data - Event data containing cart and operation ID
   */
  handleCartUpdate(data) {
    const { cart, operationId } = data || {};
    console.log('[UIUpdater] handleCartUpdate', { hasCart: !!cart, operationId, item_count: cart?.item_count });
    if (!cart) {
      Debug.log('warn', '[UIUpdater] Received empty cart data');
      return;
    }

    // Skip if we're already processing this operation
    if (operationId && this.processingOperationId === operationId) {
      console.log('[UIUpdater] Skipping duplicate (same operationId)', operationId);
      Debug.log('info', '[UIUpdater] Skipping duplicate update', {
        operationId,
        processingOperationId: this.processingOperationId
      });
      return;
    }
    
    Debug.log('info', '[UIUpdater] Processing cart update', {
      operationId,
      itemCount: cart.item_count,
      total: cart.total_price,
      queueSize: this.updateQueue.size
    });
    
    // Add update to queue with operation ID
    this.updateQueue.set(operationId || `update_${Date.now()}`, cart);
    
    // Track update request
    this.updateCount++;
    const currentUpdate = this.updateCount;
    
    // Trigger debounced update
    this.debouncedUpdate();
    
    Debug.log('info', '[UIUpdater] Queued update request', {
      operationId,
      updateId: currentUpdate,
      queueSize: this.updateQueue.size,
      lastUpdate: this.lastUpdate ? Date.now() - this.lastUpdate : null
    });
  }

  /**
   * Process queued updates
   * @private
   */
  processUpdateQueue() {
    if (!this.updateQueue.size) return;
    
    Debug.log('info', '[UIUpdater] Processing update queue', {
      queueSize: this.updateQueue.size,
      updateCount: this.updateCount,
      processingOperationId: this.processingOperationId
    });
   
    // Get latest cart state and operation ID
    const entries = Array.from(this.updateQueue.entries());
    const [operationId, cart] = entries[entries.length - 1];
    
    // Clear queue before processing to prevent recursion
    this.updateQueue.clear();
    
    // Set processing flag
    this.processingOperationId = operationId;
    
    // Batch DOM updates
    Performance.batchDOMUpdates(() => {
      try {
        // Update cart counts
        this.updateCartCount(cart);
        
        // Update cart totals
        this.updateCartTotal(cart);
        
        // Update cart state
        this.updateCartState(cart);
        
        this.lastUpdate = Date.now();
        
        Debug.log('info', '[UIUpdater] DOM updates completed', {
          itemCount: cart.item_count,
          total: cart.total_price,
          operationId: this.processingOperationId
        });
      } catch (error) {
        Debug.log('error', '[UIUpdater] Failed to update DOM', {
          error,
          stackTrace: error.stack
        });
      } finally {
        // Clear processing flag
        this.processingOperationId = null;
      }
    });
  }

  /**
   * Update cart count elements
   * @private
   * @param {Cart} cart - Cart data
   */
  updateCartCount(cart) {
    const elements = DOM.queryAll(SELECTORS.CART_COUNT);
    const count = cart.item_count || 0;
    console.log('[UIUpdater] updateCartCount', { count, selector: SELECTORS.CART_COUNT, found: elements.length });
    elements.forEach(async element => {
      if (element.textContent !== count.toString()) {
        // Use SmartUpdater for better DOM updates
        const newElement = element.cloneNode(true);
        newElement.textContent = count;
        newElement.setAttribute('data-count', count);
        
        await smartUpdater.updateElement(element, newElement, {
          preserveState: true,
          animate: true
        });
      }
    });
  }

  /**
   * Update cart total elements
   * @private
   * @param {Cart} cart - Cart data
   */
  updateCartTotal(cart) {
    const elements = DOM.queryAll(SELECTORS.CART_TOTAL);
    const totalPrice = cart.total_price || 0;
    const formattedTotal = Format.money(totalPrice, this.config.moneyFormat);
    
    elements.forEach(element => {
      if (element.textContent !== formattedTotal) {
        element.textContent = formattedTotal;
        element.setAttribute('data-total', totalPrice);
        this.addUpdateAnimation(element);
      }
    });
  }

  /**
   * Update cart subtotal elements
   * @private
   * @param {Cart} cart - Cart data
   */
  updateCartSubtotal(cart) {
    const elements = DOM.queryAll(SELECTORS.CART_SUBTOTAL);
    const subtotalPrice = cart.items_subtotal_price || 0;
    const formattedSubtotal = Format.money(subtotalPrice, this.config.moneyFormat);
    
    elements.forEach(element => {
      if (element.textContent !== formattedSubtotal) {
        element.textContent = formattedSubtotal;
        element.setAttribute('data-subtotal', subtotalPrice);
        this.addUpdateAnimation(element);
      }
    });
  }

  /**
   * Update cart state elements
   * @private
   * @param {Cart} cart - Cart data
   */
  updateCartState(cart) {
    const elements = DOM.queryAll(SELECTORS.CART_STATE);
    const isEmpty = !cart.item_count || cart.item_count === 0;
    
    elements.forEach(element => {
      element.setAttribute('data-empty', isEmpty);
      element.setAttribute('data-item-count', cart.item_count || 0);
      
      // Toggle CSS classes for styling
      element.classList.toggle('cart-empty', isEmpty);
      element.classList.toggle('cart-has-items', !isEmpty);
      
      // Update aria-label for accessibility
      const itemText = Format.pluralize(cart.item_count || 0, 'item', 'items');
      element.setAttribute('aria-label', `Shopping cart with ${cart.item_count || 0} ${itemText}`);
    });
  }

  /**
   * Update cart badge elements
   * @private
   * @param {Cart} cart - Cart data
   */
  updateCartBadges(cart) {
    const elements = DOM.queryAll(SELECTORS.CART_BADGE);
    const count = cart.item_count || 0;
    const hasItems = count > 0;
    
    elements.forEach(element => {
      if (element.textContent !== count.toString()) {
        element.textContent = count;
        element.setAttribute('data-count', count);
        element.style.display = hasItems ? '' : 'none';
        element.setAttribute('aria-hidden', !hasItems);
        
        if (hasItems) {
          this.addUpdateAnimation(element);
        }
      }
    });
  }

  /**
   * Add visual feedback animation to updated elements
   * @private
   * @param {Element} element - Element to animate
   */
  addUpdateAnimation(element) {
    if (!this.config.animateChanges) return;
    
    element.classList.add('cart-element-updated');
    
    // Remove after animation
    setTimeout(() => {
      element.classList.remove('cart-element-updated');
    }, 300);
  }

  /**
   * Force update all UI elements
   * @returns {Promise<void>}
   */
  async forceUpdate() {
    try {
      const cart = await this.cartManager.getCart();
      this.handleCartUpdate({ cart, operationId: 'forceUpdate' }); // Pass a unique operationId for force update
      Debug.log('info', 'Forced UI update completed');
    } catch (error) {
      Debug.log('error', 'Failed to force update UI', error);
    }
  }

  /**
   * Get update statistics
   * @returns {Object} Update statistics
   */
  getStats() {
    return {
      updateCount: this.updateCount,
      lastUpdate: this.lastUpdate,
      isInitialized: this.isInitialized,
      queueSize: this.updateQueue.size,
      elementsTracked: {
        count: DOM.queryAll(SELECTORS.CART_COUNT).length,
        total: DOM.queryAll(SELECTORS.CART_TOTAL).length,
        subtotal: DOM.queryAll(SELECTORS.CART_SUBTOTAL).length,
        state: DOM.queryAll(SELECTORS.CART_STATE).length,
        badges: DOM.queryAll(SELECTORS.CART_BADGE).length
      }
    };
  }

  /**
   * Destroy UI updater
   */
  destroy() {
    // Remove event listeners
    this.cartManager.off(CART_EVENTS.UPDATED, this.handleCartUpdate);
    this.cartManager.off(CART_EVENTS.LOADED, this.handleCartUpdate);
    this.cartManager.off(CART_EVENTS.ADDED, this.handleCartUpdate);
    
    // Clear update queue
    this.updateQueue.clear();
    
    // Reset state
    this.isInitialized = false;
    this.updateCount = 0;
    this.lastUpdate = null;
    this.processingOperationId = null; // Reset processing operation ID
    
    Debug.log('info', 'UIUpdater destroyed');
  }
} 